import React, { useState } from 'react';
import styled from 'styled-components';
import { position } from 'styled-system';

import { Flex, Box } from 'Components/Base';
import { FeedbackLike, FeedbackDisLike, FeedbackReport } from 'Containers/Feedback';
import { LAYOUT_TYPE } from 'Context/ClassroomContext';
import { ClassroomHeartbeatLogger } from 'Util/Analytics';

import { ClassroomLayoutView } from '../Components/ClassroomLayout';
import { Header, Footer, SubsectionContent } from '../Components';
import ClearByOnDemand from '../Featured/ClearByOnDemand/Container/ClearByOnDemand';
import ClearByLearnEd from '../Featured/ClearByLearnEd/Container/ClearByLearnEd';
import ClearFAQButton from '../Featured/ClearByOnDemand/Container/ClearPlus/ClearFAQButton';
import ClearPlusPanel from '../Featured/ClearByOnDemand/Container/ClearPlus/ClearPlusPanel';
import { useClearPlusByOnDemand } from '../Featured/ClearByOnDemand/Contexts/ClearPlusByOnDemandProvider';

const MainContentContainer = styled(({ isDrawerOpen, horizontalDrawer, ...restProps }) => (
  <Flex {...restProps} />
))`
  flex-grow: 1;
  height: 100vh;
  width: 100%;
  max-height: ${({ isDrawerOpen, horizontalDrawer }) =>
    isDrawerOpen && !horizontalDrawer ? 'calc(100vw * 9 / 16)' : '100%'};
  transition: max-height 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  ${position};
`;

const Content = ({
  layoutType,
  allowContentType,
  toggleLayoutActive,
  horizontalDrawer,
  isDrawerOpen,
  handleExitClassroom,
  isNative,
  isLearnCraftCourse,
}) => (
  <MainContentContainer
    isDrawerOpen={isDrawerOpen}
    flexDirection={'column'}
    justifyContent={'center'}
    // position={['unset', 'fixed']}
    horizontalDrawer={horizontalDrawer}
    onTouchEndCapture={(e) => {
      if (isNative) {
        toggleLayoutActive();
        e.stopPropagation();
      }
    }}
    onClick={(e) => {
      if (!isNative) {
        /**
         * This is a workaround to prevent running both onTouch and onClick at the same time on native devices.
         * The problem is video container (videojs) does not recieve onClick event on native devices, instead onTouch seems to work.
         * Therefore, we need to have onTouch event above to toggleLayoutActive and then prevent onClick from also triggered at the same time outside video zone.
         * Also, somehow, stopImmediatePropagation does not work in this case.
         *
         * TODO: Find the proper way to handle this case either make video container recongnise onClick event or make stopImmediatePropagation work.
         */
        toggleLayoutActive();
        e.stopPropagation();
      }
    }}
  >
    <Box style={{ position: 'relative' }}>
      <ClassroomHeartbeatLogger />
      <SubsectionContent
        onExitClassroom={handleExitClassroom}
        allowedTypes={[...allowContentType]}
      />
      {layoutType !== LAYOUT_TYPE.QUIZ &&
        layoutType !== LAYOUT_TYPE.EBOOK &&
        layoutType !== LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER && (
          <Box
            display={['flex', 'none']}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={1}
            px={'18px'}
            mt={3}
          >
            <Flex>
              <Flex width={'46px'} justifyContent="center" alignItems="center">
                <FeedbackLike />
              </Flex>
              <Flex width={'46px'} justifyContent="center" alignItems="center">
                <FeedbackDisLike />
              </Flex>
              <Flex width={'46px'} justifyContent="center" alignItems="center">
                <FeedbackReport />
              </Flex>
            </Flex>
            {isLearnCraftCourse ? <ClearFAQButton /> : <ClearByOnDemand />}
            <ClearByLearnEd />
          </Box>
        )}
    </Box>
  </MainContentContainer>
);

const ClassroomFullView = ({
  layoutType,
  allowContentType,
  handleExitClassroom,
  isMobile,
  isNative,
  isLearnCraftCourse,
}) => {
  const [isOpenCourseToCPanel, setIsOpenCourseToCPanel] = useState(false);
  const [isOpenCourseInfoPanel, setIsOpenCourseInfoPanel] = useState(false);
  const isHorizontalDrawer = !isMobile;

  const { isOpenDrawer, onCloseDrawer, currentSubVideoId } = useClearPlusByOnDemand();

  const handleToggleCourseInfoPanel = (bool) => {
    setIsOpenCourseInfoPanel(bool);
  };

  const handleToggleCourseToCPanel = (bool) => {
    setIsOpenCourseToCPanel(bool);
  };

  return (
    <>
      <ClassroomLayoutView
        variant={ClassroomLayoutView.VARIANTS.FULL}
        renderHeader={({ active, setLayoutActive } = { setLayoutActive: () => {} }) => (
          <Header
            onClick={() => setLayoutActive()}
            isDrawerOpen={isOpenCourseInfoPanel}
            isClassroomActive={active}
            onExitClassroom={handleExitClassroom}
            onToggleDrawer={handleToggleCourseInfoPanel}
            horizontalDrawer={isHorizontalDrawer}
          />
        )}
        renderChildren={({ toggleLayoutActive } = { toggleLayoutActive: () => {} }) => (
          <Content
            layoutType={layoutType}
            allowContentType={allowContentType}
            toggleLayoutActive={toggleLayoutActive}
            horizontalDrawer={isHorizontalDrawer}
            isDrawerOpen={isOpenCourseToCPanel || isOpenCourseInfoPanel}
            handleExitClassroom={handleExitClassroom}
            isNative={isNative}
            isLearnCraftCourse={isLearnCraftCourse}
          />
        )}
        renderFooter={({ active, setLayoutActive } = { setLayoutActive: () => {} }) => (
          <Footer
            onClick={() => setLayoutActive(7000)}
            isDrawerOpen={isOpenCourseToCPanel}
            isClassroomActive={active}
            onToggleDrawer={handleToggleCourseToCPanel}
            horizontalDrawer={isHorizontalDrawer}
            name="classroom-layout-footer"
          />
        )}
      />
      <ClearPlusPanel
        openDrawer={isOpenDrawer}
        isHorizontalDrawer={isHorizontalDrawer}
        onCloseDrawer={onCloseDrawer}
        currentSubVideoId={currentSubVideoId}
        layoutType={layoutType}
      />
    </>
  );
};

export default ClassroomFullView;
